// src/pages/Coupons.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Button, Chip, Stack, TextField,
  InputAdornment, MenuItem, Select, FormControl, InputLabel, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, Snackbar,
  CircularProgress, alpha, useTheme, Switch, Card, CardContent, Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import BarChartIcon from '@mui/icons-material/BarChart';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PublicIcon from '@mui/icons-material/Public';
import StoreIcon from '@mui/icons-material/Store';
import PeopleIcon from '@mui/icons-material/People'; // ✅ NEW
import PersonIcon from '@mui/icons-material/Person'; // ✅ NEW
import { fetchCoupons, deleteCoupon, toggleCouponActive } from '../couponApi';

export default function Coupons() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, coupon: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadCoupons = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterType !== 'all') params.type = filterType;
      if (filterStatus !== 'all') params.isActive = filterStatus === 'active' ? 'true' : 'false';
      const data = await fetchCoupons(params);
      setCoupons(data);
    } catch (err) {
      setSnackbar({ open: true, message: t('ErrorLoadingCoupons') || 'Error loading coupons', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus, t]);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  const handleToggleActive = async (coupon) => {
    try {
      await toggleCouponActive(coupon.Id);
      setSnackbar({
        open: true,
        message: coupon.IsActive
          ? (t('CouponDeactivated') || 'Coupon deactivated')
          : (t('CouponActivated') || 'Coupon activated'),
        severity: 'success'
      });
      loadCoupons();
    } catch (err) {
      setSnackbar({ open: true, message: t('ErrorToggling') || 'Error toggling coupon', severity: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCoupon(deleteDialog.coupon.Id);
      setDeleteDialog({ open: false, coupon: null });
      setSnackbar({ open: true, message: t('CouponDeleted') || 'Coupon deleted successfully', severity: 'success' });
      loadCoupons();
    } catch (err) {
      setSnackbar({ open: true, message: t('ErrorDeleting') || 'Error deleting coupon', severity: 'error' });
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setSnackbar({ open: true, message: t('CodeCopied') || 'Code copied to clipboard', severity: 'info' });
  };

  const filteredCoupons = coupons.filter(c =>
    c.Code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.Description && c.Description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getStatusChip = (coupon) => {
    const now = new Date();
    if (!coupon.IsActive) return <Chip label={t('Inactive') || 'Inactive'} size="small" color="default" />;
    if (coupon.EndDate && new Date(coupon.EndDate) < now) return <Chip label={t('Expired') || 'Expired'} size="small" color="error" />;
    if (coupon.StartDate && new Date(coupon.StartDate) > now) return <Chip label={t('Scheduled') || 'Scheduled'} size="small" color="warning" />;
    if (coupon.UsageLimit !== null && coupon.UsedCount >= coupon.UsageLimit) return <Chip label={t('UsedUp') || 'Used Up'} size="small" color="error" />;
    return <Chip label={t('Active') || 'Active'} size="small" color="success" />;
  };

  // Summary cards
  const totalCoupons = coupons.length;
  const activeCoupons = coupons.filter(c => c.IsActive).length;
  const discountCoupons = coupons.filter(c => c.Type === 'discount').length;
  const voucherCoupons = coupons.filter(c => c.Type === 'voucher').length;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {t('CouponsVouchers') || 'Coupons & Vouchers'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('ManageCouponsDesc') || 'Manage discount coupons and purchase vouchers'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/coupons/add')}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
        >
          {t('AddCoupon') || 'Add Coupon'}
        </Button>
      </Stack>

      {/* Summary Cards */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: t('TotalCoupons') || 'Total', value: totalCoupons, color: '#1976d2', icon: <LocalOfferIcon /> },
          { label: t('ActiveCoupons') || 'Active', value: activeCoupons, color: '#2e7d32', icon: <LocalOfferIcon /> },
          { label: t('DiscountCoupons') || 'Discounts', value: discountCoupons, color: '#ed6c02', icon: <LocalOfferIcon /> },
          { label: t('PurchaseVouchers') || 'Vouchers', value: voucherCoupons, color: '#9c27b0', icon: <CardGiftcardIcon /> },
        ].map((card, idx) => (
          <Grid item xs={6} sm={3} key={idx}>
            <Card elevation={0} sx={{ border: `1px solid ${alpha(card.color, 0.2)}`, borderRadius: 2 }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                    <Typography variant="h4" fontWeight={700} color={card.color}>{card.value}</Typography>
                  </Box>
                  <Box sx={{ color: alpha(card.color, 0.3), fontSize: 40 }}>{card.icon}</Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            size="small"
            placeholder={t('SearchCoupons') || 'Search by code or description...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              )
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t('Type') || 'Type'}</InputLabel>
            <Select value={filterType} onChange={(e) => setFilterType(e.target.value)} label={t('Type') || 'Type'}>
              <MenuItem value="all">{t('All') || 'All'}</MenuItem>
              <MenuItem value="discount">{t('Discount') || 'Discount'}</MenuItem>
              <MenuItem value="voucher">{t('Voucher') || 'Voucher'}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t('Status') || 'Status'}</InputLabel>
            <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} label={t('Status') || 'Status'}>
              <MenuItem value="all">{t('All') || 'All'}</MenuItem>
              <MenuItem value="active">{t('Active') || 'Active'}</MenuItem>
              <MenuItem value="inactive">{t('Inactive') || 'Inactive'}</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : filteredCoupons.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <LocalOfferIcon sx={{ fontSize: 60, color: alpha('#000', 0.15), mb: 2 }} />
          <Typography variant="h6" color="text.secondary">{t('NoCouponsFound') || 'No coupons found'}</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700 }}>{t('Code') || 'Code'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('Type') || 'Type'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('Value') || 'Value'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('Scope') || 'Scope'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('AssignedTo') || 'Assigned To'}</TableCell>{/* ✅ NEW */}
                <TableCell sx={{ fontWeight: 700 }}>{t('Usage') || 'Usage'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('Status') || 'Status'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('Active') || 'Active'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">{t('Actions') || 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCoupons.map((coupon) => (
                <TableRow key={coupon.Id} hover>
                  {/* Code */}
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip
                        label={coupon.Code}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main'
                        }}
                      />
                      <Tooltip title={t('CopyCode') || 'Copy code'}>
                        <IconButton size="small" onClick={() => handleCopyCode(coupon.Code)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    {coupon.Description && (
                      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                        {coupon.Description}
                      </Typography>
                    )}
                  </TableCell>

                  {/* Type */}
                  <TableCell>
                    <Chip
                      icon={coupon.Type === 'discount' ? <LocalOfferIcon /> : <CardGiftcardIcon />}
                      label={coupon.Type === 'discount' ? (t('Discount') || 'Discount') : (t('Voucher') || 'Voucher')}
                      size="small"
                      color={coupon.Type === 'discount' ? 'warning' : 'secondary'}
                      variant="outlined"
                    />
                  </TableCell>

                  {/* Value */}
                  <TableCell>
                    <Typography fontWeight={600}>{coupon.FormattedValue}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {coupon.DiscountType === 'percentage' ? (t('Percentage') || 'Percentage') : (t('FixedAmount') || 'Fixed Amount')}
                    </Typography>
                  </TableCell>

                  {/* Scope (Branches) */}
                  <TableCell>
                    {coupon.IsGlobal ? (
                      <Chip icon={<PublicIcon />} label={t('AllBranches') || 'All Branches'} size="small" color="info" variant="outlined" />
                    ) : (
                      <Tooltip title={coupon.Branches.map(b => b.Name).join(', ')}>
                        <Chip
                          icon={<StoreIcon />}
                          label={`${coupon.Branches.length} ${t('Branches') || 'Branches'}`}
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                  </TableCell>

                  {/* ✅ NEW: Assigned Users */}
                  <TableCell>
                    {coupon.IsForAllUsers ? (
                      <Chip
                        icon={<PeopleIcon />}
                        label={t('AllUsers') || 'All Users'}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    ) : (
                      <Tooltip
                        title={
                          coupon.AssignedUsers.map(u =>
                            `${u.Name || u.PhoneNumber || u.Email || `#${u.Id}`}`
                          ).join(', ')
                        }
                      >
                        <Chip
                          icon={<PersonIcon />}
                          label={`${coupon.AssignedUsers.length} ${t('Users') || 'Users'}`}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                  </TableCell>

                  {/* Usage */}
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {coupon.UsedCount} / {coupon.UsageLimit || '∞'}
                    </Typography>
                    {coupon.RemainingUses !== null && (
                      <Typography variant="caption" color="text.secondary">
                        {coupon.RemainingUses} {t('Remaining') || 'remaining'}
                      </Typography>
                    )}
                  </TableCell>

                  {/* Status */}
                  <TableCell>{getStatusChip(coupon)}</TableCell>

                  {/* Active Toggle */}
                  <TableCell>
                    <Switch
                      checked={coupon.IsActive}
                      onChange={() => handleToggleActive(coupon)}
                      size="small"
                      color="success"
                    />
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title={t('Statistics') || 'Statistics'}>
                        <IconButton size="small" color="info" onClick={() => navigate(`/admin/coupons/stats/${coupon.Id}`)}>
                          <BarChartIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('Edit') || 'Edit'}>
                        <IconButton size="small" color="primary" onClick={() => navigate(`/admin/coupons/edit/${coupon.Id}`)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('Delete') || 'Delete'}>
                        <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, coupon })}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, coupon: null })}>
        <DialogTitle>{t('DeleteCoupon') || 'Delete Coupon'}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('DeleteCouponConfirm') || 'Are you sure you want to delete coupon'}{' '}
            <strong>{deleteDialog.coupon?.Code}</strong>?
          </Typography>
          {deleteDialog.coupon?.UsedCount > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('CouponHasUsage') || 'This coupon has been used'} {deleteDialog.coupon?.UsedCount} {t('Times') || 'times'}. {t('UsageHistoryWillBeDeleted') || 'Usage history will also be deleted.'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, coupon: null })}>{t('Cancel') || 'Cancel'}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">{t('Delete') || 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}